import React from "react";
import { useTranslation } from "react-i18next";
import { getActivityStepText } from "./activityStepText";
import {
  formatDuration,
  shouldShowDuration,
  useElapsedSeconds,
} from "./elapsedTime";
import "./ActivityStepInline.css";

// `executionStartedAt` (Tarefa 9, FR-019): timestamp do início da 1ª etapa desta
// execução (não o início desta etapa específica) - o indicador inline mostra o
// tempo TOTAL decorrido, ao vivo enquanto a execução está em andamento.
//
// `hideDuration` (Tarefa 3, specs/front_planning_tools_ajustes_finais/plan.md, US3):
// o painel de atividades já mostra esse mesmo tempo (mesma âncora `executionStartedAt`/
// `activity_started_at`) quando está aberto para esta mensagem - sem isso, os dois
// relógios ficavam visíveis ao mesmo tempo, cada um com seu próprio `setInterval`,
// podendo mostrar segundos diferentes num dado instante. O texto da etapa
// ("Acionando skill X") continua aparecendo - só o número duplicado some.
function ActivityStepInline({
  step,
  onClick,
  executionStartedAt,
  hideDuration = false,
}) {
  const { t: tChat } = useTranslation("chat");

  const isInProgress = step?.status === "in_progress";
  // `step?.durationMs`: sem isso, useElapsedSeconds cai no fallback `Date.now() -
  // startedAt` mesmo com isInProgress=false (frozenMs teria que ser um número pra
  // congelar de verdade) - o tempo continuava subindo a cada re-render, mesmo com a
  // etapa já em 'error' (achado 2026-09-10).
  const elapsedSeconds = useElapsedSeconds(
    executionStartedAt,
    isInProgress,
    step?.durationMs,
  );

  if (!step) {
    return null;
  }

  const isError = step.status === "error";
  const text = getActivityStepText(step, tChat, { withSkillName: true });
  const isClickable = typeof onClick === "function";

  // Achado de acessibilidade (specs/front_planning_tools/plan.md): `role="button"` num
  // `<div>` não ganha de graça o comportamento de Enter/Espaço acionarem o clique - isso só
  // é automático num `<button>` real. Sem esse handler, quem navega só por teclado nunca
  // conseguia abrir o painel de Atividade por aqui.
  const handleKeyDown = isClickable
    ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event);
        }
      }
    : undefined;

  return (
    <div
      className={`ActivityStepInline${isInProgress ? " shimmer" : ""}${isError ? " error" : ""}${isClickable ? " clickable" : ""}`}
      data-testid="activity-step-inline"
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {isError && (
        // Mesmo ícone do painel lateral (ActivityPanel/index.js, STEP_ICON_BY_TYPE +
        // caso error) - antes era um "!" em texto, diferente do ícone usado no painel
        // pra mesma etapa em erro (achado 2026-09-10).
        <img
          className="ActivityStepInline-error-icon"
          src="/imagens/planning-erro.svg"
          alt=""
          data-testid="activity-step-inline-error-icon"
        />
      )}
      {isInProgress && (
        <svg
          className="ActivityStepInline-icon"
          data-testid="activity-step-inline-icon"
          width="20"
          height="8"
          viewBox="0 0 20 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            className="ActivityStepInline-dot"
            cx="2"
            cy="6"
            r="2"
            fill="#0067FF"
          />
          <circle
            className="ActivityStepInline-dot"
            cx="10"
            cy="2"
            r="2"
            fill="#FD6B3F"
          />
          <circle
            className="ActivityStepInline-dot"
            cx="18"
            cy="5"
            r="2"
            fill="#0067FF"
          />
        </svg>
      )}
      <span
        className="ActivityStepInline-text"
        data-testid="activity-step-inline-text"
      >
        {text}
      </span>
      {shouldShowDuration(elapsedSeconds) && !hideDuration && (
        <span
          className="ActivityStepInline-duration"
          data-testid="activity-step-inline-duration"
        >
          · {formatDuration(elapsedSeconds)}
        </span>
      )}
    </div>
  );
}

export default ActivityStepInline;
